import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type Job } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { NetworkGraph } from '../components/NetworkGraph';
import { useJobs } from '../components/JobToast';

type Tab = 'overview' | 'timeline' | 'evidence' | 'entities' | 'network' | 'alerts' | 'tasks' | 'bulk';

interface CaseDetail {
  id: string;
  name: string;
  description: string | null;
  status: string;
}

export function CaseWorkspacePage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<Tab>('overview');
  const [caze, setCaze] = useState<CaseDetail | null>(null);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [members, setMembers] = useState<Array<{ id: string; name: string; email: string; case_role: string }>>([]);
  const [canManageMembers, setCanManageMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const data = await api.get<{
      case: CaseDetail;
      members: Array<{ id: string; name: string; email: string; case_role: string }>;
      stats: Record<string, number>;
      permissions?: { canManageMembers?: boolean };
    }>(`/api/cases/${id}`);
    setCaze(data.case);
    setMembers(data.members);
    setStats(data.stats);
    setCanManageMembers(Boolean(data.permissions?.canManageMembers));
  }, [id]);

  useEffect(() => {
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Laden mislukt'));
  }, [load]);

  const setStatus = async (status: string) => {
    await api.patch(`/api/cases/${id}`, { status });
    await load();
  };

  if (error) return <div className="alert error">{error}</div>;
  if (!caze) return <div className="muted">Zaak laden…</div>;

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'overview', label: 'Overzicht' },
    { id: 'timeline', label: 'Tijdlijn' },
    { id: 'evidence', label: 'Dossier' },
    { id: 'entities', label: 'Entiteiten' },
    { id: 'network', label: 'Netwerk' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'tasks', label: 'Taken' },
    { id: 'bulk', label: 'Bulk-inladen' },
  ];

  return (
    <div>
      <div className="row" style={{ marginBottom: 8 }}>
        <Link to="/cases" className="muted">
          ← Zaken
        </Link>
        <div className="spacer" />
        <a className="btn btn-ghost btn-sm" href={`/api/investigate/export/${id}`}>
          Export dossier
        </a>
      </div>
      <h1>{caze.name}</h1>
      <p className="page-lead">{caze.description || 'Geen omschrijving'}</p>

      <div className="row" style={{ marginBottom: 16 }}>
        <span className={`badge ${caze.status === 'open' ? 'ok' : caze.status === 'paused' ? 'warn' : ''}`}>
          {caze.status}
        </span>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => void setStatus('open')}>
          Open
        </button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => void setStatus('paused')}>
          Pauze
        </button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => void setStatus('closed')}>
          Sluiten
        </button>
      </div>

      <div className="grid-stats">
        <div className="stat"><div className="label">Bronnen</div><div className="value">{stats.sources ?? 0}</div></div>
        <div className="stat"><div className="label">Berichten</div><div className="value">{stats.messages ?? 0}</div></div>
        <div className="stat"><div className="label">Dossier</div><div className="value">{stats.evidence ?? 0}</div></div>
        <div className="stat"><div className="label">Open taken</div><div className="value">{stats.tasksOpen ?? 0}</div></div>
      </div>

      <div className="tab-bar">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === 'alerts' && (stats.alertsUnseen ?? 0) > 0 ? ` (${stats.alertsUnseen})` : ''}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          caseId={id}
          members={members}
          canManageMembers={canManageMembers}
          onRefresh={() => void load()}
        />
      )}
      {tab === 'timeline' && <TimelineTab caseId={id} />}
      {tab === 'evidence' && <EvidenceTab caseId={id} />}
      {tab === 'entities' && <EntitiesTab caseId={id} />}
      {tab === 'network' && <NetworkTab caseId={id} />}
      {tab === 'alerts' && <AlertsTab caseId={id} onRefresh={() => void load()} />}
      {tab === 'tasks' && <TasksTab caseId={id} />}
      {tab === 'bulk' && <BulkTab caseId={id} />}
    </div>
  );
}

function OverviewTab({
  caseId,
  members,
  canManageMembers,
  onRefresh,
}: {
  caseId: string;
  members: Array<{ id: string; name: string; email: string; case_role: string }>;
  canManageMembers: boolean;
  onRefresh: () => void;
}) {
  const [sources, setSources] = useState<
    Array<{
      id: number;
      handle: string;
      health: string;
      message_count: number;
      last_success_at: string | null;
      last_ingest_at: string | null;
      last_error: string | null;
    }>
  >([]);
  const [available, setAvailable] = useState<Array<{ id: number; handle: string; message_count: number; title: string | null }>>([]);
  const [directory, setDirectory] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [pick, setPick] = useState('');
  const [memberPick, setMemberPick] = useState('');
  const [memberRole, setMemberRole] = useState<'owner' | 'lead' | 'analyst' | 'viewer'>('analyst');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);

  const loadSources = async () => {
    const linked = await api.get<{ sources: typeof sources }>(`/api/sources?caseId=${encodeURIComponent(caseId)}`);
    setSources(linked.sources);
    const free = await api.get<{ sources: typeof available }>('/api/sources?unassigned=1');
    setAvailable(free.sources);
  };

  useEffect(() => {
    void loadSources();
    if (canManageMembers) {
      void api
        .get<{ users: Array<{ id: string; name: string; email: string }> }>('/api/users/directory')
        .then((d) => setDirectory(d.users))
        .catch(() => undefined);
    }
  }, [caseId, canManageMembers]);

  const attach = async () => {
    if (!pick) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.patch(`/api/sources/${pick}`, { caseId });
      setPick('');
      setMsg('Bron gekoppeld — bestaande berichten horen nu bij deze zaak (geen her-inladen nodig).');
      await loadSources();
      onRefresh();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Koppelen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const detach = async (id: number) => {
    if (!confirm('Bron loskoppelen van deze zaak? Berichten blijven bewaard.')) return;
    await api.patch(`/api/sources/${id}`, { caseId: null });
    await loadSources();
    onRefresh();
  };

  const addMember = async () => {
    if (!memberPick) return;
    setMemberError(null);
    try {
      await api.post(`/api/cases/${caseId}/members`, { userId: memberPick, role: memberRole });
      setMemberPick('');
      onRefresh();
    } catch (e) {
      setMemberError(e instanceof Error ? e.message : 'Toevoegen mislukt');
    }
  };

  const setRole = async (userId: string, role: string) => {
    setMemberError(null);
    try {
      await api.patch(`/api/cases/${caseId}/members/${userId}`, { role });
      onRefresh();
    } catch (e) {
      setMemberError(e instanceof Error ? e.message : 'Rol wijzigen mislukt');
    }
  };

  const removeMember = async (userId: string, name: string) => {
    if (!confirm(`${name} verwijderen uit deze zaak?`)) return;
    setMemberError(null);
    try {
      await api.delete(`/api/cases/${caseId}/members/${userId}`);
      onRefresh();
    } catch (e) {
      setMemberError(e instanceof Error ? e.message : 'Verwijderen mislukt');
    }
  };

  const memberIds = new Set(members.map((m) => m.id));
  const candidates = directory.filter((u) => !memberIds.has(u.id));

  return (
    <div className="panel">
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Team</h2>
      {memberError && <div className="alert error">{memberError}</div>}
      <table className="table">
        <thead>
          <tr>
            <th>Naam</th>
            <th>Rol</th>
            {canManageMembers && <th />}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.id}>
              <td>
                {m.name}
                <div className="muted">{m.email}</div>
              </td>
              <td>
                {canManageMembers ? (
                  <select
                    className="select input-md"
                    value={m.case_role}
                    onChange={(e) => void setRole(m.id, e.target.value)}
                  >
                    <option value="owner">Eigenaar</option>
                    <option value="lead">Lead</option>
                    <option value="analyst">Analist</option>
                    <option value="viewer">Viewer</option>
                  </select>
                ) : (
                  <span className="badge">{m.case_role}</span>
                )}
              </td>
              {canManageMembers && (
                <td>
                  <button className="btn btn-danger btn-sm" type="button" onClick={() => void removeMember(m.id, m.name)}>
                    Verwijderen
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {canManageMembers && (
        <div className="toolbar" style={{ marginTop: 12, marginBottom: 8 }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Persoon toevoegen</label>
            <select className="select" value={memberPick} onChange={(e) => setMemberPick(e.target.value)}>
              <option value="">Kies gebruiker…</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Rol</label>
            <select
              className="select input-md"
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value as typeof memberRole)}
            >
              <option value="owner">Eigenaar</option>
              <option value="lead">Lead</option>
              <option value="analyst">Analist</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <button className="btn" type="button" disabled={!memberPick} onClick={() => void addMember()}>
            Toevoegen
          </button>
        </div>
      )}
      {canManageMembers && candidates.length === 0 && (
        <p className="muted">Alle actieve gebruikers zitten al in deze zaak.</p>
      )}
      {!canManageMembers && (
        <p className="muted">Alleen eigenaren (of de aanmaker) kunnen leden beheren.</p>
      )}

      <h2 style={{ fontSize: '1.05rem' }}>Bronnen bij deze zaak</h2>
      <p className="muted">
        Koppel bestaande, al ingeladen bronnen — je hoeft niet opnieuw te bulken. Berichten van gekoppelde
        bronnen verschijnen in de tijdlijn; pin er een naar <em>Dossier</em> als bewijs.
      </p>

      <div className="toolbar" style={{ marginBottom: 12 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Bestaande bron koppelen</label>
          <select className="select" value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Kies een bron…</option>
            {available.map((s) => (
              <option key={s.id} value={s.id}>
                {s.handle} ({s.message_count} berichten){s.title ? ` — ${s.title}` : ''}
              </option>
            ))}
          </select>
        </div>
        <button className="btn" type="button" disabled={!pick || busy} onClick={() => void attach()}>
          Koppelen
        </button>
      </div>
      {available.length === 0 && (
        <p className="muted">Geen losse bronnen meer — alles is al aan een zaak gekoppeld, of er zijn nog geen bronnen.</p>
      )}
      {msg && <div className="alert ok">{msg}</div>}

      {sources.length === 0 && <p className="muted">Nog geen bronnen aan deze zaak gekoppeld.</p>}
      <table className="table">
        <thead>
          <tr>
            <th>Handle</th>
            <th>Berichten</th>
            <th>Status</th>
            <th>Laatst bijgewerkt</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => (
            <tr key={s.id}>
              <td>{s.handle}</td>
              <td>
                <Link to={`/search?source=${encodeURIComponent(s.handle)}&browse=1`}>{s.message_count}</Link>
              </td>
              <td>
                <span className={`badge ${s.health === 'ok' ? 'ok' : s.health === 'error' ? 'danger' : 'warn'}`}>
                  {s.health}
                </span>
              </td>
              <td className="muted">
                {s.last_ingest_at || s.last_success_at
                  ? formatDateTime(s.last_ingest_at || s.last_success_at || '')
                  : '—'}
              </td>
              <td>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => void detach(s.id)}>
                  Loskoppelen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="btn btn-ghost btn-sm" type="button" onClick={() => { void loadSources(); onRefresh(); }}>
        Vernieuwen
      </button>
    </div>
  );
}

function TimelineTab({ caseId }: { caseId: string }) {
  const [messages, setMessages] = useState<Array<{ id: number; date_utc: string; text: string | null; handle: string; permalink: string | null }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .get<{ messages: typeof messages }>(`/api/investigate/timeline?caseId=${encodeURIComponent(caseId)}`)
      .then((d) => setMessages(d.messages));
  }, [caseId]);

  const addEvidence = async (messageId: number) => {
    setBusy(true);
    try {
      await api.post('/api/investigate/evidence', { caseId, messageId });
      alert('Toegevoegd aan dossier');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="msg-list">
      {messages.map((m) => (
        <article className="msg" key={m.id}>
          <div className="msg-head">
            <span>{m.handle} · {formatDateTime(m.date_utc)}</span>
            <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => void addEvidence(m.id)}>
              Naar dossier
            </button>
          </div>
          {m.permalink && <a href={m.permalink} target="_blank" rel="noopener noreferrer">Permalink</a>}
          <pre className="msg-text">{m.text || '[Media]'}</pre>
        </article>
      ))}
      {messages.length === 0 && <div className="empty">Nog geen berichten voor deze zaak.</div>}
    </div>
  );
}

function EvidenceTab({ caseId }: { caseId: string }) {
  const [items, setItems] = useState<Array<{ id: number; title: string | null; note: string | null; snapshot_text: string | null; snapshot_source: string | null; snapshot_date_utc: string | null; snapshot_permalink: string | null; created_at: string }>>([]);
  const [note, setNote] = useState('');

  const load = () =>
    api.get<{ evidence: typeof items }>(`/api/investigate/evidence?caseId=${encodeURIComponent(caseId)}`).then((d) => setItems(d.evidence));

  useEffect(() => {
    void load();
  }, [caseId]);

  const addNote = async (e: FormEvent) => {
    e.preventDefault();
    await api.post('/api/investigate/evidence', { caseId, title: 'Notitie', note });
    setNote('');
    await load();
  };

  const remove = async (id: number) => {
    await api.delete(`/api/investigate/evidence/${id}`);
    await load();
  };

  return (
    <div>
      <form className="panel toolbar" onSubmit={(e) => void addNote(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Dossiernotitie</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bevinding / toelichting…" required />
        </div>
        <button className="btn" type="submit">Toevoegen</button>
      </form>
      <div className="msg-list">
        {items.map((e) => (
          <article className="msg" key={e.id}>
            <div className="msg-head">
              <span>
                <strong>{e.title}</strong>
                {e.snapshot_source ? ` · ${e.snapshot_source}` : ''}
                {e.snapshot_date_utc ? ` · ${formatDateTime(e.snapshot_date_utc)}` : ` · ${formatDateTime(e.created_at)}`}
              </span>
              <button className="btn btn-danger btn-sm" type="button" onClick={() => void remove(e.id)}>Verwijderen</button>
            </div>
            {e.note && <p>{e.note}</p>}
            {e.snapshot_permalink && <a href={e.snapshot_permalink} target="_blank" rel="noopener noreferrer">{e.snapshot_permalink}</a>}
            {e.snapshot_text && <pre className="msg-text">{e.snapshot_text}</pre>}
          </article>
        ))}
      </div>
    </div>
  );
}

function EntitiesTab({ caseId }: { caseId: string }) {
  const { trackJob } = useJobs();
  const [entities, setEntities] = useState<Array<{ id: number; type: string; value: string; mention_count: number; label: string | null }>>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get<{ entities: typeof entities }>(`/api/investigate/entities?caseId=${encodeURIComponent(caseId)}`)
      .then((d) => setEntities(d.entities));

  useEffect(() => {
    void load();
  }, [caseId]);

  const extract = async () => {
    setBusy(true);
    try {
      await api.post('/api/investigate/entities/extract', { caseId, limit: 2000 });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const runOcr = async () => {
    setBusy(true);
    try {
      const { job } = await api.post<{ job: Job }>('/api/jobs/ocr-backfill', { caseId, limit: 300 });
      await trackJob(job.id, 'OCR media');
      await api.post('/api/investigate/entities/extract', { caseId, limit: 2000 });
      await load();
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(
    () => entities.filter((e) => !filter || e.type === filter),
    [entities, filter],
  );

  return (
    <div>
      <div className="row" style={{ marginBottom: 12 }}>
        <button className="btn" type="button" disabled={busy} onClick={() => void extract()}>
          {busy ? 'Bezig…' : 'Extraheer uit berichten'}
        </button>
        <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => void runOcr()}>
          OCR op media
        </button>
        <select className="select input-md" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">Alle types</option>
          {['phone', 'email', 'url', 'handle', 'hashtag', 'plate', 'iban', 'telegram_link'].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className="panel" style={{ padding: 0, overflow: 'auto' }}>
        <table className="table">
          <thead><tr><th>Type</th><th>Waarde</th><th>Hits</th></tr></thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id}>
                <td><span className="badge">{e.type}</span></td>
                <td>{e.label || e.value}</td>
                <td>{e.mention_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NetworkTab({ caseId }: { caseId: string }) {
  const [edges, setEdges] = useState<Array<{ source: string; target: string; weight: number }>>([]);
  const [nodes, setNodes] = useState<Array<{ id: string; kind: string; weight: number; health?: string }>>([]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<{ edges: typeof edges; nodes: typeof nodes }>(`/api/investigate/network?caseId=${encodeURIComponent(caseId)}`)
      .then((d) => {
        setEdges(d.edges);
        setNodes(d.nodes);
      });
  }, [caseId]);

  const related = selected
    ? edges.filter((e) => e.source === selected || e.target === selected).slice(0, 30)
    : [];

  return (
    <div className="panel">
      <p className="muted">Interactief netwerk van bronnen en @handles. Klik/sleep knopen om te verkennen.</p>
      {nodes.length > 0 ? (
        <NetworkGraph nodes={nodes} edges={edges} onSelect={setSelected} />
      ) : (
        <div className="empty">Nog geen netwerkdata — laad berichten in en run entiteit-extractie.</div>
      )}
      {selected && (
        <div style={{ marginTop: 12 }}>
          <strong>Focus: {selected}</strong>
          <div className="network-list" style={{ marginTop: 8 }}>
            {related.map((e, i) => (
              <div className="network-edge" key={`${e.source}-${e.target}-${i}`}>
                <span className="badge">{e.source}</span>
                <span className="muted">→</span>
                <span className="badge ok">{e.target}</span>
                <span className="muted">×{e.weight}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlertsTab({ caseId, onRefresh }: { caseId: string; onRefresh: () => void }) {
  const [alerts, setAlerts] = useState<Array<{ id: number; name: string; query: string; active: number; unseen: number }>>([]);
  const [hits, setHits] = useState<Array<{ id: number; alert_name: string; handle: string; text: string | null; date_utc: string; seen: number }>>([]);
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editQuery, setEditQuery] = useState('');
  const [info, setInfo] = useState<string | null>(null);

  const load = async () => {
    const a = await api.get<{ alerts: typeof alerts }>(`/api/investigate/alerts?caseId=${encodeURIComponent(caseId)}`);
    setAlerts(a.alerts);
    const h = await api.get<{ hits: typeof hits }>(`/api/investigate/alert-hits?caseId=${encodeURIComponent(caseId)}&unseen=1`);
    setHits(h.hits);
  };

  useEffect(() => {
    void load();
  }, [caseId]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const res = await api.post<{ id: number; matched: number }>('/api/investigate/alerts', {
      caseId,
      name,
      query,
      backfill: true,
    });
    setName('');
    setQuery('');
    setInfo(`Alert aangemaakt — ${res.matched} treffer(s) in bestaande berichten.`);
    await load();
    onRefresh();
  };

  const startEdit = (a: { id: number; name: string; query: string }) => {
    setEditId(a.id);
    setEditName(a.name);
    setEditQuery(a.query);
  };

  const saveEdit = async () => {
    if (editId == null) return;
    const res = await api.patch<{ matched?: number }>(`/api/investigate/alerts/${editId}`, {
      name: editName,
      query: editQuery,
      backfill: true,
    });
    setInfo(`Alert bijgewerkt — ${res.matched ?? 0} nieuwe treffer(s) in bestaande berichten.`);
    setEditId(null);
    await load();
    onRefresh();
  };

  const remove = async (id: number) => {
    if (!confirm('Alert verwijderen? Hits verdwijnen mee.')) return;
    await api.delete(`/api/investigate/alerts/${id}`);
    await load();
    onRefresh();
  };

  const toggle = async (a: { id: number; active: number }) => {
    await api.patch(`/api/investigate/alerts/${a.id}`, { active: !a.active });
    await load();
  };

  const markSeen = async () => {
    await api.post('/api/investigate/alert-hits/seen', { all: true });
    await load();
    onRefresh();
  };

  return (
    <div>
      <form className="panel toolbar" onSubmit={(e) => void create(e)}>
        <div className="field"><label>Naam</label><input className="input input-md" value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div className="field" style={{ flex: 1 }}><label>Trefwoorden (AND)</label><input className="input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="rotterdam dreiging" required /></div>
        <button className="btn" type="submit">Alert aanmaken</button>
      </form>
      {info && <div className="alert ok">{info}</div>}
      <div className="row" style={{ margin: '12px 0' }}>
        <strong>Actieve alerts</strong>
        <div className="spacer" />
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => void markSeen()}>Alles gelezen</button>
      </div>
      <div className="msg-list">
        {alerts.map((a) => (
          <article className="msg" key={a.id}>
            {editId === a.id ? (
              <div className="toolbar">
                <div className="field"><label>Naam</label><input className="input input-md" value={editName} onChange={(e) => setEditName(e.target.value)} /></div>
                <div className="field" style={{ flex: 1 }}><label>Query</label><input className="input" value={editQuery} onChange={(e) => setEditQuery(e.target.value)} /></div>
                <button className="btn btn-sm" type="button" onClick={() => void saveEdit()}>Opslaan</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setEditId(null)}>Annuleren</button>
              </div>
            ) : (
              <>
                <div className="msg-head">
                  <span>
                    <strong>{a.name}</strong> — <code>{a.query}</code>{' '}
                    {!a.active && <span className="badge warn">uit</span>}
                    {a.unseen ? <span className="badge warn">{a.unseen} nieuw</span> : null}
                  </span>
                  <div className="row">
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => startEdit(a)}>Bewerken</button>
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => void toggle(a)}>
                      {a.active ? 'Pauzeren' : 'Activeren'}
                    </button>
                    <button className="btn btn-danger btn-sm" type="button" onClick={() => void remove(a.id)}>Verwijderen</button>
                  </div>
                </div>
              </>
            )}
          </article>
        ))}
      </div>
      <h2 style={{ fontSize: '1.05rem' }}>Nieuwe hits</h2>
      <div className="msg-list">
        {hits.map((h) => (
          <article className="msg" key={h.id}>
            <div className="msg-head"><span>{h.alert_name} · {h.handle} · {formatDateTime(h.date_utc)}</span></div>
            <pre className="msg-text">{h.text}</pre>
          </article>
        ))}
        {hits.length === 0 && <div className="empty">Geen ongelezen hits.</div>}
      </div>
    </div>
  );
}

function TasksTab({ caseId }: { caseId: string }) {
  const [tasks, setTasks] = useState<Array<{ id: number; title: string; status: string; priority: string; description: string | null }>>([]);
  const [title, setTitle] = useState('');

  const load = () => api.get<{ tasks: typeof tasks }>(`/api/investigate/tasks?caseId=${encodeURIComponent(caseId)}`).then((d) => setTasks(d.tasks));

  useEffect(() => {
    void load();
  }, [caseId]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    await api.post('/api/investigate/tasks', { caseId, title });
    setTitle('');
    await load();
  };

  const setStatus = async (id: number, status: string) => {
    await api.patch(`/api/investigate/tasks/${id}`, { status });
    await load();
  };

  return (
    <div>
      <form className="panel toolbar" onSubmit={(e) => void create(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Nieuwe taak</label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Bijv. 12 kanalen inhalen" />
        </div>
        <button className="btn" type="submit">Toevoegen</button>
      </form>
      <div className="msg-list">
        {tasks.map((t) => (
          <article className="msg" key={t.id}>
            <div className="msg-head">
              <span><strong>{t.title}</strong> · <span className="badge">{t.status}</span> · {t.priority}</span>
              <div className="row">
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => void setStatus(t.id, 'doing')}>Bezig</button>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => void setStatus(t.id, 'done')}>Klaar</button>
              </div>
            </div>
            {t.description && <p className="muted">{t.description}</p>}
          </article>
        ))}
      </div>
    </div>
  );
}

function BulkTab({ caseId }: { caseId: string }) {
  const [handles, setHandles] = useState('');
  const [limit, setLimit] = useState(100);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post<{ queued: number }>('/api/investigate/bulk-ingest', { handles, caseId, limit });
      setResult(`${res.queued} jobs in de wachtrij gezet.`);
      setHandles('');
    } catch (err) {
      setResult(err instanceof Error ? err.message : 'Mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel form-col" onSubmit={(e) => void submit(e)}>
      <p className="muted">
        Alleen voor <strong>nieuwe</strong> handles die je nog niet hebt. Heb je bronnen al globaal
        ingeladen? Koppel ze via <em>Overzicht → Bestaande bron koppelen</em> — dan hergebruik je de
        berichten zonder opnieuw te downloaden.
      </p>
      <textarea className="textarea" rows={8} value={handles} onChange={(e) => setHandles(e.target.value)} placeholder={"@kanaal1\n@kanaal2"} required />
      <div className="field">
        <label>Limiet per kanaal</label>
        <input className="input input-sm" type="number" min={1} max={2000} value={limit} onChange={(e) => setLimit(Number(e.target.value))} />
      </div>
      <button className="btn" type="submit" disabled={busy}>{busy ? 'Bezig…' : 'Inladen starten'}</button>
      {result && <div className="alert ok">{result}</div>}
    </form>
  );
}
